/**
 * The GOMPA Verified badge — and, on the detail page, the claim entry point.
 *
 * A separate, unrelated concept from `monastery.badge` (the free-text
 * heritage tag, e.g. "UNESCO site") — the two are meant to sit side by side,
 * not merge, so this renders its own tag rather than replacing that one.
 *
 * States, in the order a monastery actually moves through them:
 *   unverified / claim_rejected  → "Unclaimed", a clickable pill to
 *                                  `claimHref` — but only where a claim link
 *                                  is meaningful (the detail page); on a card
 *                                  in a grid it renders nothing, same as
 *                                  before, so browsing isn't cluttered with
 *                                  claim prompts for every unlisted monastery.
 *   claim_pending / _under_review → "Claim pending", inert — a claim already
 *                                  exists, so there is nothing left to click.
 *   reviewed                     → "Reviewed by GOMPA", a lighter tag.
 *   verified                     → "GOMPA Verified", green, with the
 *                                  click-to-explain popover.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import InfoBadge from './InfoBadge';

const CLAIMABLE_STATUSES = new Set(['unverified', 'claim_rejected']);
const PENDING_STATUSES = new Set(['claim_pending', 'claim_under_review']);

export default function VerificationBadge({ status, explain = false, claimHref }) {
  const { t } = useTranslation();
  const effectiveStatus = status ?? 'unverified';

  if (claimHref && CLAIMABLE_STATUSES.has(effectiveStatus)) {
    return (
      <Link to={claimHref} className="tag tag--plain">
        <Icon name="shield" size={12} />
        {t('claim.unclaimedBadge', 'Unclaimed')}
      </Link>
    );
  }

  if (claimHref && PENDING_STATUSES.has(effectiveStatus)) {
    return (
      <span className="tag tag--plain" style={{ opacity: 0.65 }}>
        <Icon name="time" size={12} />
        {t('claim.pendingBadge', 'Claim pending')}
      </span>
    );
  }

  if (effectiveStatus !== 'verified' && effectiveStatus !== 'reviewed') return null;

  const verified = effectiveStatus === 'verified';
  const label = verified
    ? t('verification.verifiedLabel', 'GOMPA Verified')
    : t('verification.reviewedLabel', 'Reviewed by GOMPA');
  const tagClass = `tag${verified ? ' tag--verified' : ' tag--plain'}`;

  if (!explain) {
    return (
      <span className={tagClass}>
        <Icon name="check" size={12} />
        {label}
      </span>
    );
  }

  return (
    <InfoBadge
      tone={verified ? 'verified' : 'plain'}
      className={tagClass}
      icon="check"
      label={label}
      title={t('verification.explainerTitle', 'What does GOMPA Verified mean?')}
      body={t(
        'verification.explainerBody',
        'GOMPA has taken additional steps to confirm this monastery and the information associated with its profile.'
      )}
    />
  );
}
